/**
 * CIBIL code -> label mappings, aligned with the official
 * "CIBIL Product JSON Description Guide V3.5".
 *
 * Codes are never inferred inline in components; anything unknown falls back
 * to a generic "<Kind> <code>" label so the UI never invents a meaning.
 */

/** Account Type Codes tab (also used for Enquiry Purpose). */
export const ACCOUNT_TYPE_CODES: Record<string, string> = {
  "01": "Auto Loan (Personal)",
  "02": "Housing Loan",
  "03": "Property Loan",
  "04": "Loan Against Shares/Securities",
  "05": "Personal Loan",
  "06": "Consumer Loan",
  "07": "Gold Loan",
  "08": "Education Loan",
  "09": "Loan to Professional",
  "10": "Credit Card",
  "11": "Leasing",
  "12": "Overdraft",
  "13": "Two-wheeler Loan",
  "14": "(NFCF) Non-Funded Credit Facility",
  "15": "(LABD) Current Loan Against Bank Deposits",
  "16": "Fleet Card",
  "17": "Commercial Vehicle Loan",
  "18": "Telco - Wireless",
  "19": "Telco - Broadband",
  "20": "Telco - Landline",
  "21": "Seller Financing",
  "23": "GECL Loan Secured",
  "24": "GECL Loan Unsecured",
  "31": "Secured Credit Card",
  "32": "Used Car Loan",
  "33": "Construction Equipment Loan",
  "34": "Tractor Loan",
  "35": "Corporate Credit Card",
  "36": "Kisan Credit Card",
  "37": "Loan on Credit Card",
  "38": "Prime Minister Jaan Dhan Yojana - Overdraft",
  "39": "Mudra Loans - Shishu / Kishor / Tarun",
  "40": "Microfinance - Business Loan",
  "41": "Microfinance - Personal Loan",
  "42": "Microfinance - Housing Loan",
  "43": "Microfinance - Other",
  "44": "Pradhan Mantri Awas Yojana - Credit Link Subsidy Scheme (MAY CLSS)",
  "45": "P2P Personal Loan",
  "46": "P2P Auto Loan",
  "47": "P2P Education Loan",
  "50": "Business Loan - Secured",
  "51": "Business Loan - General",
  "52": "(BLPS-SB) Business Loan - Priority Sector - Small Business",
  "53": "(BLPS-AGR) Business Loan - Priority Sector - Agriculture",
  "54": "(BLPS-OTH) Business Loan - Priority Sector - Others",
  "55": "(BNFCF-GEN) Business Non-Funded Credit Facility - General",
  "56": "(BNFCF-PS-SB) Business Non-Funded Credit Facility - Priority Sector - Small Business",
  "57": "(BNFCF-PS-AGR) Business Non-Funded Credit Facility - Priority Sector - Agriculture",
  "58": "(BNFCF-PS-OTH) Business Non-Funded Credit Facility - Priority Sector - Others",
  "59": "(BLABD) Current Business Loan Against Bank Deposits",
  "61": "Business Loan - Unsecured",
  "67": "Buy Now Pay Later",
  "69": "Short Term Personal Loan",
  "70": "Priority Sector - Gold Loan",
  "71": "Temporary Overdraft",
  "98": "Secured",
  "99": "Unsecured",
  "00": "Other",
};

/** Codes valid for Enquiry Purpose only. */
export const ENQUIRY_ONLY_PURPOSE_CODES: Record<string, string> = {
  "80": "Microfinance Detailed Report",
  "81": "Summary Report",
  "88": "Locate Plus for Insurance",
  "90": "Account Review",
  "91": "Retro Enquiry",
  "92": "Locate Plus",
  "97": "Adviser Liability",
};

export const ENQUIRY_PURPOSE_CODES: Record<string, string> = {
  ...ACCOUNT_TYPE_CODES,
  ...ENQUIRY_ONLY_PURPOSE_CODES,
};

/** Ownership Indicator (AccountDesignator). */
export const OWNERSHIP_CODES: Record<string, string> = {
  "1": "Individual",
  "2": "Authorized User",
  "3": "Guarantor",
  "4": "Joint",
  "5": "Deceased",
};

/** Payment Frequency. */
export const PAYMENT_FREQUENCY_CODES: Record<string, string> = {
  "01": "Weekly",
  "02": "Fortnightly",
  "03": "Monthly",
  "04": "Quarterly",
  "05": "Bullet Payment",
  "06": "Daily",
  "07": "Half Yearly",
  "08": "Yearly",
  "09": "On-demand",
};

/** Type of Collateral. */
export const COLLATERAL_TYPE_CODES: Record<string, string> = {
  "00": "No Collateral",
  "01": "Property",
  "02": "Gold",
  "03": "Shares",
  "04": "Saving Account and Fixed Deposit",
  "05": "Multiple Securities",
  "06": "Others",
};

/** AccountCondition where abbreviation = "creditFacilityStatus". */
export const CREDIT_FACILITY_STATUS_CODES: Record<string, string> = {
  "00": "Restructured Loan",
  "01": "Restructured Loan (Govt. Mandated)",
  "02": "Written-off",
  "03": "Settled",
  "04": "Post (WO) Settled",
  "05": "Account Sold",
  "06": "Written Off and Account Sold",
  "07": "Account Purchased",
  "08": "Account Purchased and Written Off",
  "09": "Account Purchased and Settled",
  "10": "Account Purchased and Restructured",
  "11": "Restructured due to Natural Calamity",
  "12": "Restructured due to COVID-19",
  "13": "Post Write Off Closed",
  "14": "Restructured & Closed",
  "15": "Auctioned & Settled",
  "16": "Repossessed & Settled",
  "17": "Guarantee Invoked",
};

/** AccountCondition where abbreviation = "suitFiledStatus". */
export const SUIT_FILED_STATUS_CODES: Record<string, string> = {
  "00": "No Suit Filed",
  "01": "Suit Filed",
  "02": "Wilful Default",
  "03": "Suit Filed (Wilful Default)",
};

/** Statuses that indicate no negative condition and should not be shown as a remark. */
export const NEUTRAL_CONDITION_LABELS = new Set(["No Suit Filed"]);

/**
 * Payment-history Asset Classification values. When one of these is reported
 * instead of a Number of Days Past Due (NDPD), it is a classification, never
 * a DPD count.
 */
export const ASSET_CLASSIFICATION_CODES: Record<string, string> = {
  STD: "Standard",
  SMA: "Special Mention Account",
  SUB: "Substandard",
  DBT: "Doubtful",
  LSS: "Loss",
  XXX: "Not Reported",
};

/** Asset classifications that represent stress on the account, worst last. */
export const ADVERSE_ASSET_CLASSIFICATIONS = ["SMA", "SUB", "DBT", "LSS"];

/** Pay-status values that are explicitly NOT day-past-due counts. */
export const NON_DPD_PAY_STATUS = new Set([
  "",
  "0",
  "000",
  "-1",
  "XXX",
  "STD",
  "SMA",
  "SUB",
  "DBT",
  "LSS",
  "NEW",
  "CLSD",
  "*",
]);

/** ID types returned in the Identifier segment. SocialId is internal to CIBIL. */
export const ID_TYPE_CODES: Record<string, string> = {
  TaxId: "Income Tax ID Number (PAN)",
  PassportId: "Passport Number",
  VoterId: "Voter ID Number",
  DriversLicenseId: "Driver's License Number",
  RationCardId: "Ration Card Number",
  CkycId: "CKYC",
  NregaId: "NREGA Card Number",
  UniversalId: "Universal ID Number (UID)",
};

/** ID types that must never be surfaced to a consumer. */
export const HIDDEN_ID_TYPES = new Set(["SocialId"]);

/** Account (TL) segment dispute error codes. */
export const ACCOUNT_ERROR_CODES: Record<string, string> = {
  "0": "No Disputes",
  "3": "Account Number in Dispute",
  "4": "Account Type in Dispute",
  "5": "Ownership Indicator in Dispute",
  "8": "Date Opened/Disbursed in Dispute",
  "9": "Date of Last Payment in Dispute",
  "10": "Date Closed in Dispute",
  "11": "Date Reported and Certified in Dispute",
  "12": "High Credit/Sanctioned Amount in Dispute",
  "13": "Current Balance in Dispute",
  "14": "Amount Overdue in Dispute",
  "30": "Payment History Start Date in Dispute",
  "31": "Payment History End Date in Dispute",
  "32": "Suit Filed / Wilful Default in Dispute",
  "33": "Written-off and Settled Status in Dispute",
  "34": "Value of Collateral in Dispute",
  "35": "Type of Collateral in Dispute",
  "36": "Credit Limit in Dispute",
  "37": "Cash Limit in Dispute",
  "38": "Rate Of Interest in Dispute",
  "39": "Repayment Tenure in Dispute",
  "40": "EMI Amount in Dispute",
  "41": "Written-off Amount (Total) in Dispute",
  "42": "Written-off Amount (Principal) in Dispute",
  "43": "Settlement Amount in Dispute",
  "44": "Payment Frequency in Dispute",
  "45": "Actual Payment Amount in Dispute",
  "886": "Duplicate Account",
  "888": "Account Ownership Error",
  "999": "Multiple Disputes in Account (TL) Segment",
  ...Object.fromEntries(
    Array.from({ length: 36 }, (_, i) => [String(101 + i), `Payment History ${i + 1} in Dispute`]),
  ),
};

/** Consumer segment (PN/ID/PT/EC/PA/IQ/ZZ) dispute error codes. */
export const SEGMENT_ERROR_CODES: Record<string, Record<string, string>> = {
  PN: {
    "1": "Consumer Name in Dispute",
    "7": "Date of Birth in Dispute",
    "8": "Gender in Dispute",
    "201": "Consumer Name - Dispute accepted - Pending corrections by the Member",
    "207": "Date of Birth - Dispute accepted - Pending corrections by the Member",
    "208": "Gender - Dispute accepted - Pending corrections by the Member",
    "998": "Multiple Disputes in Name (PN) Segment - Dispute accepted - Pending corrections by the Member",
    "999": "Multiple Disputes in Name (PN) Segment",
  },
  ID: {
    "1": "ID Type in Dispute",
    "2": "ID Number in Dispute",
    "3": "Issue Date in Dispute",
    "4": "Expiration Date in Dispute",
    "201": "ID Type - Dispute accepted - Pending corrections by the Member",
    "202": "ID Number - Dispute accepted - Pending corrections by the Member",
    "203": "Issue Date - Dispute accepted - Pending corrections by the Member",
    "204": "Expiration Date - Dispute accepted - Pending corrections by the Member",
    "998": "Multiple Disputes in Identification (ID) Segment - Dispute accepted - Pending corrections by the Member",
    "999": "Multiple Disputes in Identification (ID) Segment",
  },
  PT: {
    "1": "Telephone Number in Dispute",
    "2": "Telephone Extension in Dispute",
    "3": "Telephone Type in Dispute",
    "201": "Telephone Number - Dispute accepted - Pending corrections by the Member",
    "202": "Telephone Extension - Dispute accepted - Pending corrections by the Member",
    "203": "Telephone Type - Dispute accepted - Pending corrections by the Member",
    "998": "Multiple Disputes in Telephone (PT) Segment - Dispute accepted - Pending corrections by the Member",
    "999": "Multiple Disputes in Telephone (PT) Segment",
  },
  EC: {
    "1": "E-Mail ID in Dispute",
    "201": "E-Mail ID - Dispute accepted - Pending corrections by the Member",
    "998": "Multiple Disputes in Email Contact (EC) Segment - Dispute accepted - Pending corrections by the Member",
    "999": "Multiple Disputes in Email Contact (EC) Segment",
  },
  PA: {
    "1": "Address Line (except State Code and PIN Code) in Dispute",
    "6": "State in Dispute",
    "7": "PIN Code in Dispute",
    "8": "Address Category in Dispute",
    "9": "Residence Code in Dispute",
    "201": "Address Line - Dispute accepted - Pending corrections by the Member",
    "206": "State - Dispute accepted - Pending corrections by the Member",
    "207": "PIN Code - Dispute accepted - Pending corrections by the Member",
    "208": "Address Category - Dispute accepted - Pending corrections by the Member",
    "209": "Residence Code - Dispute accepted - Pending corrections by the Member",
    "998": "Multiple Disputes in Address (PA) Segment - Dispute accepted - Pending corrections by the Member",
    "999": "Multiple Disputes in Address (PA) Segment",
  },
  IQ: {
    "1": "Enquiry Purpose in Dispute",
    "6": "Enquiry Amount in Dispute",
    "201": "Enquiry Purpose - Dispute accepted - Pending corrections by the Member",
    "206": "Enquiry Amount - Dispute accepted - Pending corrections by the Member",
    "887": "Enquiry Ownership Error - Dispute accepted - Pending corrections by the Member",
    "888": "Enquiry Ownership Error",
    "998": "Multiple Disputes in Enquiry (IQ) Segment - Dispute accepted - Pending corrections by the Member",
    "999": "Multiple Disputes in Enquiry (IQ) Segment",
  },
  ZZ: {
    "0": "No Dispute",
    "998": "Multiple Disputes in multiple segments - Dispute accepted - Pending corrections by the Member",
    "999": "Multiple Disputes in multiple segments",
  },
};

/**
 * CIBIL remarks codes. Prefix (PN/ID/PT/EC/PA/TL/IQ/ZZ) + 4 digits, plus the
 * miscellaneous numeric codes.
 */
export const CIBIL_REMARKS_CODES: Record<string, string> = {
  PN0001:
    "Certain information under Personal / Contract / Enquiry information section has been disputed by the consumer.",
  "000001": "One or more Members have not responded to your Dispute",
  "000002": "Dispute accepted - pending correction by the Member",
  ZZ0999: "Multiple Disputes in multiple segments",
  ...Object.fromEntries(
    Object.entries(SEGMENT_ERROR_CODES).flatMap(([seg, map]) =>
      Object.entries(map).map(([code, label]) => [
        `${seg}1${code.padStart(3, "0")}`,
        label,
      ]),
    ),
  ),
  ...Object.fromEntries(
    Object.entries(ACCOUNT_ERROR_CODES).map(([code, label]) => [
      `TL1${code.padStart(3, "0")}`,
      label,
    ]),
  ),
};

export const DPD_BUCKETS = [
  { key: "lt30", label: "< 30 days", min: 1, max: 29 },
  { key: "30_60", label: "30-60 days", min: 30, max: 59 },
  { key: "60_90", label: "60-90 days", min: 60, max: 89 },
  { key: "90_180", label: "90-180 days", min: 90, max: 179 },
  { key: "gte180", label: ">= 180 days", min: 180, max: Number.POSITIVE_INFINITY },
] as const;

export type DpdBucketKey = (typeof DPD_BUCKETS)[number]["key"] | "all";

/** riskScore value that means the consumer has no credit history. */
export const NO_HISTORY_SCORE = 1;

export const NO_HISTORY_EXPLAINER =
  "You may not be eligible for a CIBIL Score because you have a credit card or loan account with no credit activity in the last three years, lenders have made enquiries but there is no credit activity, or you only hold add-on credit cards with no credit exposure.";

export function codeLabel(
  map: Record<string, string>,
  code: string | null | undefined,
  kind = "Type",
): string | null {
  if (!code) return null;
  const padded = code.length === 1 ? `0${code}` : code;
  const unpadded = code.replace(/^0+/, "");
  return map[code] ?? map[padded] ?? map[unpadded] ?? `${kind} ${code}`;
}

/** Decodes an AccountCondition node using its `abbreviation` discriminator. */
export function accountConditionLabel(
  symbol: string | null | undefined,
  abbreviation: string | null | undefined,
): string | null {
  if (!symbol) return null;
  const abbr = (abbreviation ?? "").trim();
  if (abbr === "suitFiledStatus") {
    return codeLabel(SUIT_FILED_STATUS_CODES, symbol, "Suit Filed Status");
  }
  if (abbr === "creditFacilityStatus") {
    return codeLabel(CREDIT_FACILITY_STATUS_CODES, symbol, "Credit Facility Status");
  }
  // No discriminator supplied: prefer the credit-facility set, which is the
  // wider of the two, and fall back to a generic label.
  return codeLabel(CREDIT_FACILITY_STATUS_CODES, symbol, "Account Condition");
}

/** Label for a payment-history status that is an Asset Classification. */
export function assetClassificationLabel(status: string | null | undefined): string | null {
  if (!status) return null;
  return ASSET_CLASSIFICATION_CODES[status.trim().toUpperCase()] ?? null;
}

export function bucketForDpd(dpd: number): string | null {
  const b = DPD_BUCKETS.find((x) => dpd >= x.min && dpd <= x.max);
  return b ? b.key : null;
}
